import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { FOLDER_LIST, gradientStyle } from './gradients';

// Messy "before" chips — large, overlapping, varying opacity; they drift
// (mf-float-*) and fade toward the beam via the column's left→right mask.
// top = % of canvas height, left/w = px (canvas ≈ reference width).
// Opacity floors at ~0.9: the beam-fade comes from the mask (which axe ignores),
// while axe DOES fold element opacity into its contrast math — so lower values
// dropped zinc-600 chip text below AA (MF-205). The mask still carries the fade.
const CHIPS: { label: string; top: string; left: number; w: number; anim: string; op: number; red?: boolean }[] = [
  { label: 'Untitled tab', top: '18%', left: 16, w: 188, anim: 'mf-float-a', op: 0.98 },
  { label: 'unsorted link', top: '29%', left: 80, w: 168, anim: 'mf-float-c', op: 0.9 },
  { label: '404 — not found', top: '40%', left: 44, w: 196, anim: 'mf-float-b', op: 1, red: true },
  { label: 'example.com (imported)', top: '56%', left: 12, w: 206, anim: 'mf-float-c', op: 0.94 },
  { label: 'duplicate · duplicate', top: '68%', left: 62, w: 188, anim: 'mf-float-d', op: 0.9 },
];

const FADE_MASK = 'linear-gradient(90deg, #000 62%, transparent)';

export function HomeChaosOrder() {
  const { t } = useTranslation();

  return (
    // Pure-decoration illustration (chaos→order metaphor). aria-hidden keeps the
    // fake chip labels out of the a11y tree, but does NOT exempt them from the
    // axe color-contrast audit (visible text is still checked), so chip opacity
    // floors at ~0.9 and light-mode labels use AA-passing zinc-500 (MF-205).
    <div
      aria-hidden
      className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-white/70 shadow-sm sm:h-[440px] dark:border-white/10 dark:bg-white/[0.015] dark:shadow-[0_40px_120px_-40px_rgba(0,0,0,0.85)]"
    >
      {/* faint grid texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 text-zinc-900 opacity-[0.04] dark:text-white dark:opacity-[0.05] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:34px_34px]"
      />

      {/* ===== MOBILE: stacked chaos → MarkFlow → order (sm:hidden) ===== */}
      <div className="relative z-[2] flex flex-col gap-5 p-5 sm:hidden">
        {/* chaos */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-zinc-400/70 dark:bg-zinc-600" />
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
              {t('home.hero.chaosLabel')}
            </span>
          </div>
          <div className="relative h-[150px] overflow-hidden">
            {CHIPS.map((c) => (
              <div key={c.label} className={cn('absolute', c.anim)} style={{ top: c.top, left: c.left, width: c.w, opacity: c.op }}>
                <div
                  className={cn(
                    'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 shadow-sm',
                    c.red
                      ? 'border-red-300 bg-white dark:border-red-500/25 dark:bg-[#1c1c22]'
                      : 'border-zinc-200 bg-white dark:border-white/[0.08] dark:bg-[#1c1c22]',
                  )}
                >
                  <span
                    className={cn(
                      'h-[18px] w-[18px] shrink-0 rounded-[5px]',
                      c.red ? 'bg-red-500 dark:bg-[#7f1d1d]' : 'bg-zinc-200 dark:bg-[#3a3a44]',
                    )}
                  />
                  <span className="truncate text-[13px] font-medium text-zinc-600 dark:text-zinc-300">{c.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MarkFlow beam divider */}
        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-[linear-gradient(90deg,transparent,rgba(139,92,246,0.5))]" />
          <span className="font-display flex items-center gap-1.5 whitespace-nowrap rounded-full border border-violet-500/30 bg-white/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-600 shadow-[0_0_20px_rgba(139,92,246,0.4)] backdrop-blur dark:bg-violet-500/15 dark:text-violet-200">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            MarkFlow
          </span>
          <span className="h-px flex-1 bg-[linear-gradient(90deg,rgba(217,70,239,0.5),transparent)]" />
        </div>

        {/* order */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-500 dark:text-violet-300">
              {t('home.hero.orderLabel')}
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500 shadow-[0_0_10px_#8b5cf6]" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {FOLDER_LIST.map((g) => (
              <div
                key={g.letter}
                className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#121216]"
              >
                <div className="flex h-10 items-center px-2.5 font-display text-sm font-bold text-white" style={gradientStyle(g)}>
                  {g.letter}
                </div>
                <div className="space-y-1.5 p-2">
                  <div className="h-1.5 w-4/5 rounded bg-zinc-200 dark:bg-white/10" />
                  <div className="h-1.5 w-3/5 rounded bg-zinc-200 dark:bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* state labels (sm+ absolute scene) */}
      <div className="absolute left-5 top-4 z-[3] hidden items-center gap-2 sm:left-7 sm:top-5 sm:flex">
        <span className="h-1.5 w-1.5 rounded-full bg-zinc-400/70 dark:bg-zinc-600" />
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 sm:text-xs dark:text-zinc-400">
          {t('home.hero.chaosLabel')}
        </span>
      </div>
      <div className="absolute right-5 top-4 z-[3] hidden items-center gap-2 sm:right-7 sm:top-5 sm:flex">
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-500 sm:text-xs dark:text-violet-300">
          {t('home.hero.orderLabel')}
        </span>
        <span className="h-1.5 w-1.5 rounded-full bg-violet-500 shadow-[0_0_10px_#8b5cf6]" />
      </div>

      {/* LEFT: messy chips (masked column, sm+ only — phones use the stacked scene above) */}
      <div
        className="absolute inset-y-0 left-0 hidden w-[40%] overflow-hidden sm:block"
        style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }}
      >
        {CHIPS.map((c) => (
          <div key={c.label} className={cn('absolute', c.anim)} style={{ top: c.top, left: c.left, width: c.w, opacity: c.op }}>
            <div
              className={cn(
                'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 shadow-sm',
                c.red
                  ? 'border-red-300 bg-white dark:border-red-500/25 dark:bg-[#1c1c22]'
                  : 'border-zinc-200 bg-white dark:border-white/[0.08] dark:bg-[#1c1c22]',
              )}
            >
              <span
                className={cn(
                  'h-[18px] w-[18px] shrink-0 rounded-[5px]',
                  c.red ? 'bg-red-500 dark:bg-[#7f1d1d]' : 'bg-zinc-200 dark:bg-[#3a3a44]',
                )}
              />
              <span className="truncate text-[13px] font-medium text-zinc-600 dark:text-zinc-300">{c.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* CENTER: AI beam + label (sm+ only) */}
      <div
        aria-hidden
        className="mf-beam absolute inset-y-0 left-[47%] hidden w-[3px] rounded-full bg-[linear-gradient(180deg,transparent,#8b5cf6_22%,#c026d3_78%,transparent)] shadow-[0_0_28px_6px_rgba(139,92,246,0.5)] sm:block"
      />
      <div className="mf-spark absolute left-[47%] top-1/2 z-[3] hidden -translate-x-1/2 -translate-y-1/2 sm:block">
        <span className="font-display flex items-center gap-1.5 whitespace-nowrap rounded-full border border-violet-500/30 bg-white/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-600 shadow-[0_0_28px_rgba(139,92,246,0.5)] backdrop-blur dark:bg-violet-500/15 dark:text-violet-200">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          MarkFlow
        </span>
      </div>

      {/* RIGHT: ordered grid — kept depersonalized (placeholder rows), sm+ only */}
      <div className="absolute right-3 top-14 bottom-5 left-[54%] hidden grid-cols-2 content-center gap-3 sm:right-6 sm:top-16 sm:grid sm:grid-cols-3">
        {FOLDER_LIST.map((g) => (
          <div
            key={g.letter}
            className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#121216]"
          >
            <div className="flex h-11 items-center px-3 font-display text-base font-bold text-white" style={gradientStyle(g)}>
              {g.letter}
            </div>
            <div className="space-y-1.5 p-2.5">
              <div className="h-1.5 w-4/5 rounded bg-zinc-200 dark:bg-white/10" />
              <div className="h-1.5 w-3/5 rounded bg-zinc-200 dark:bg-white/10" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
